import { type ClipboardEvent, type FormEvent, type KeyboardEvent, type MouseEvent, useEffect, useRef } from 'react'

import type { FieldValues, Path, PathValue } from 'react-hook-form'
import { get, useFormContext } from 'react-hook-form'

import { useUnique } from '../../Unique'
import { useMergedRefs } from '../../useMergedRefs'
import type { CommonFieldProps } from '../CommonProps'
import { useFormOptions } from '../useFormOptions'

type Props<TPath extends string> = Omit<
	CommonFieldProps<TPath>,
	'label' | 'hint' | 'isFullWidth' | 'className' | 'style' | 'testId'
> & {
	autoComplete?: string
	type?: 'text' | 'number'
	columns?: number
}

export const usePartitioned = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	isRequired = false,
	type = 'text',
	isDisabled = false,
	name,
	isReadOnly = false,
	showOptionalHint: fieldShowOptionalHint,
	placeholder,
	shouldAutofocus = false,
	validate = {},
	columns = 6,
	autoComplete = 'off',
}: Props<TPath>) => {
	const id = useUnique(name)
	const { showOptionalHint } = useFormOptions()
	const {
		register,
		setValue,
		formState: { errors },
		watch,
		trigger,
	} = useFormContext<TFieldValues>()
	const fieldValue = watch(name, undefined)
	const inputRef = useRef<HTMLInputElement>(null)
	const inputRefs = useRef<Array<HTMLInputElement | null>>([])
	const registeredInput = register(name, {
		required: isRequired ? 'This field is required' : false,
		disabled: isDisabled,
		validate,
		maxLength: {
			value: columns,
			message: `Cannot be more than ${columns} characters long`,
		},
		minLength: {
			value: columns,
			message: `Needs to be at least ${columns} characters long`,
		},
		setValueAs: (value: string) => (value.trim() === '' ? undefined : value),
	})
	const error = get(errors, name)
	const hasError = Boolean(error)
	const labelId = `${id}-label`
	const ref = useMergedRefs<HTMLInputElement>(registeredInput.ref, inputRef)

	useEffect(() => {
		const value = typeof fieldValue === 'string' ? fieldValue : ''
		inputRefs.current.forEach((input, index) => {
			if (input && input.value !== value[index]) input.value = value[index] ?? ''
		})
	}, [fieldValue])

	return {
		id,
		labelId,
		fieldValue,
		error,
		hasError,
		inputRef,
		showOptionalHint,
		fieldShowOptionalHint,
		inputProps: {
			...registeredInput,
			ref,
			id,
			minLength: columns,
			maxLength: columns,
			'aria-labelledby': labelId,
			hidden: true,
		},
		getInputProps: (index: number) => ({
			id: `${id}[${index}]`,
			name: `${name}[${index}]`,
			inputMode: type === 'number' ? ('numeric' as const) : ('text' as const),
			maxLength: 1,
			minLength: 1,
			tabIndex: index !== 0 ? -1 : undefined,
			type: 'text' as const,
			autoComplete,
			autoFocus: index === 0 && shouldAutofocus,
			disabled: isDisabled,
			readOnly: isReadOnly,
			placeholder: placeholder && !fieldValue ? placeholder[index] : undefined,
			'aria-labelledby': labelId,
			ref: (input: HTMLInputElement | null) => {
				inputRefs.current[index] = input
			},
			onBlur: registeredInput.onBlur,
			onClick: (event: MouseEvent<HTMLInputElement>) => event.currentTarget.select(),
			onInput: (event: FormEvent<HTMLInputElement>) => {
				const input = event.currentTarget
				setValue(
					name,
					inputRefs.current.map((input) => input?.value ?? '').join('') as PathValue<TFieldValues, TPath>,
					{
						shouldDirty: true,
					},
				)
				if (index !== columns - 1 && input.value !== '') {
					inputRefs.current[index + 1]?.focus()
					inputRefs.current[index + 1]?.select()
				}
				if (hasError) trigger(name)
			},
			onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
				event.preventDefault()
				const pastedContent = (event.clipboardData?.getData('text') ?? '').slice(0, columns)
				setValue(name, pastedContent as PathValue<TFieldValues, TPath>, {
					shouldDirty: true,
				})
				if (hasError) trigger(name)
				inputRefs.current[Math.min(pastedContent.length, columns - 1)]?.focus()
			},
			onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
				switch (event.key) {
					case 'Backspace':
					case 'Delete':
						if (inputRefs.current[index]?.value === '' && index !== 0) inputRefs.current[index - 1]?.focus()
						break
					case 'ArrowLeft':
						if (index !== 0) inputRefs.current[index - 1]?.focus()
						break
					case 'ArrowRight':
						if (index < columns - 1) inputRefs.current[index + 1]?.focus()
						break
					default:
						if (/^\s$/.test(event.key)) event.preventDefault()
				}
			},
		}),
	}
}
